import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IEntityChatMember as IOrderChatMember, IEntityChatMessage as IOrderChatMessage } from 'src/components/EntityChatView/interface';
import { getOrderChatMembers, getOrderChatMessages, markOrderChatAsRead, sendCustomerMessage } from 'src/services/ChatService';

export interface OrderChatState {
    messages: IOrderChatMessage[];
    members: IOrderChatMember[];
    isLoading: boolean;
    isSending: boolean;
    isMembersLoading: boolean;
    // In-flight guards. ChatView is mounted TWICE on the order detail page (the Messages tab and
    // the Messages widget) and both share this single slice, so every lifecycle fetch used to go
    // out twice. These flags back the `condition` option on the thunks below, which drops the
    // duplicate dispatch instead of firing a second identical request.
    isMarkingAsRead: boolean;
    isCatchingUp: boolean;
    hasMoreMessages: boolean;
    lastMessageId: string | number | null;
    chatGroupId: string;
    unreadCount: number;
    hiddenMessageIds: (string | number)[];
    isSocketRoomJoined: boolean;
}

const initialState: OrderChatState = {
    messages: [],
    members: [],
    isLoading: false,
    isSending: false,
    isMembersLoading: false,
    isMarkingAsRead: false,
    isCatchingUp: false,
    hasMoreMessages: false,
    lastMessageId: null,
    chatGroupId: '',
    unreadCount: 0,
    hiddenMessageIds: [],
    isSocketRoomJoined: false,
};

/**
 * Normalize a raw API message into IOrderChatMessage shape
 */
const normalizeMessage = (raw: any): IOrderChatMessage => {
    const senderData = raw?.sender || raw?.userInfo || {};

    let content = '';

    if (typeof raw?.content === 'object' && raw?.content !== null) {
        content = raw.content?.message || '';
    } else {
        content = raw?.content || raw?.message || '';
    }

    return {
        id: raw?.id?.toString() || `${Date.now()}`,
        content,
        userInfo: {
            id: raw?.senderId || senderData?.id || '',
            firstName: senderData?.firstName || '',
            lastName: senderData?.lastName || '',
            imageUrl: senderData?.imageUrl || '',
            // Socket payload (wal-backend-v2) sends imageUrl + image as separate fields.
            // API payload (wal-customer-portal-be) sends image as a full URL already (virtual field).
            // If imageUrl exists, combine them; otherwise image is already a full URL.
            image: senderData?.imageUrl && senderData?.image ? `${senderData.imageUrl}${senderData.image}` : senderData?.image || '',
            user_status: senderData?.user_status || 'offline',
        },
        createdAt: raw?.createdAt || new Date().toISOString(),
        isUnread: raw?.isUnread || false,
        isOptimistic: false,
        hasError: false,
        messageType: raw?.messageType || 'text',
        attachments: raw?.attachments || [],
        senderType: raw?.senderType || 'user',
        chatType: raw?.chatType || 'customer',
    };
};

/**
 * Merge one raw incoming message into state, deduplicating by id and reconciling the optimistic
 * copy of a message this client just sent. Shared by the socket handler and the catch-up fetch so
 * both paths apply identical dedupe rules.
 */
const mergeIncomingMessage = (state: OrderChatState, raw: any) => {
    const normalized = normalizeMessage(raw);

    // Deduplicate: skip if this ID already exists as a non-optimistic message
    const existingIndex = state.messages.findIndex((m: IOrderChatMessage) => m.id?.toString() === normalized.id?.toString());

    if (existingIndex >= 0 && !state.messages[existingIndex].isOptimistic) {
        return;
    }

    // If it matches an optimistic message by ID, replace it
    if (existingIndex >= 0 && state.messages[existingIndex].isOptimistic) {
        state.messages[existingIndex] = normalized;

        return;
    }

    // Try to match by content + senderType (replace pending optimistic for current user)
    const senderId = raw?.senderId?.toString();
    const optimisticIndex = state.messages.findIndex(
        (m: IOrderChatMessage) => m.isOptimistic && !m.hasError && m.userInfo?.id?.toString() === senderId && m.content?.trim() === normalized.content?.trim() && m.senderType === 'customer'
    );

    if (optimisticIndex >= 0) {
        state.messages[optimisticIndex] = normalized;

        return;
    }

    state.messages.push(normalized);
};

export const fetchOrderChatMessages = createAsyncThunk(
    'orderChat/fetchMessages',
    async ({ orderId, lastId }: { orderId: string | number; lastId?: string | number | null }) => {
        const response: any = await getOrderChatMessages(orderId, lastId ? { lastId } : undefined);
        const data = response?.data || response;
        const messages: any[] = data?.messages || data?.data?.messages || [];
        const hasMore: boolean = data?.hasMore ?? data?.data?.hasMore ?? data?.pagination?.hasMore ?? false;
        // API returns messages DESC (newest first). Use nextCursor (oldest message ID in batch)
        // as the cursor for fetching the next page of older messages.
        const nextLastId = data?.pagination?.nextCursor ?? data?.data?.pagination?.nextCursor ?? null;
        // Extract chatGroupId so sendMessage can pass it as groupId
        const chatGroupId: string = data?.chatGroupId || data?.data?.chatGroupId || '';

        // Reverse to ASC order (oldest → newest) so newest messages render at the bottom
        const normalizedMessages = messages.map(normalizeMessage).reverse();

        return { messages: normalizedMessages, hasMore, lastId: nextLastId, isPagination: !!lastId, chatGroupId };
    },
    {
        // Only the initial (non-paginated) page is deduplicated — two ChatView instances mount in
        // the same commit and both ask for page 1. Pagination requests carry a distinct `lastId`
        // and must always go through.
        condition: ({ lastId }, { getState }) => {
            if (lastId) return true;

            return !(getState() as any).orderChat.isLoading;
        },
    }
);

export const fetchOrderChatMembers = createAsyncThunk(
    'orderChat/fetchMembers',
    async (orderId: string | number) => {
        const response: any = await getOrderChatMembers(orderId);
        const data = response?.data || response;
        const members: any[] = data?.members || data?.data?.members || data || [];

        return members.map((m: any) => ({
            id: m?.id,
            firstName: m?.firstName || '',
            lastName: m?.lastName || '',
            imageUrl: m?.imageUrl || '',
            image: m?.image || '',
            user_status: m?.user_status || 'offline',
            isOnline: m?.user_status === 'online',
        })) as IOrderChatMember[];
    },
    {
        condition: (_orderId, { getState }) => !(getState() as any).orderChat.isMembersLoading,
    }
);

export const markOrderChatMessagesAsRead = createAsyncThunk(
    'orderChat/markAsRead',
    async (orderId: string | number) => {
        await markOrderChatAsRead(orderId);
    },
    {
        // Fired from several places at once on open: the widget's isActive effect, the autofocus
        // handler it triggers 50ms later, and the Messages tab when it becomes active. They all
        // write the same lastReadAt, so collapsing concurrent calls loses nothing.
        condition: (_orderId, { getState }) => !(getState() as any).orderChat.isMarkingAsRead,
    }
);

/**
 * Re-fetch the newest page and merge it in (deduped by id).
 * Covers messages that landed in the gap between the mount fetch and the socket room join, and
 * after a reconnect. Guarded so the tab and the widget — which join the same room in the same
 * commit — do not both issue it.
 */
export const catchUpOrderChatMessages = createAsyncThunk(
    'orderChat/catchUpMessages',
    async (orderId: string | number) => {
        try {
            const response: any = await getOrderChatMessages(orderId);
            const data = response?.data || response;
            const rawMessages: any[] = data?.messages || data?.data?.messages || [];

            // API returns DESC (newest first). Reverse to ASC so the messages append in order.
            return [...rawMessages].reverse();
        } catch (error) {
            // Non-fatal — the thread already has the mount fetch's page and live socket updates.
            // Logged (not toasted) because this runs in the background on every room join.
            console.error('Chat catch-up fetch failed:', { orderId, error });
            throw error;
        }
    },
    {
        condition: (_orderId, { getState }) => !(getState() as any).orderChat.isCatchingUp,
    }
);

const orderChatSlice = createSlice({
    name: 'orderChat',
    initialState,
    reducers: {
        addSocketMessage: (state, action: PayloadAction<any>) => {
            mergeIncomingMessage(state, action.payload);
        },
        incrementUnreadCount: (state) => {
            state.unreadCount += 1;
        },
        updateMemberStatus: (state, action: PayloadAction<{ memberId: string | number; status: string }>) => {
            const { memberId, status } = action.payload;
            const member = state.members.find((m: IOrderChatMember) => m.id?.toString() === memberId?.toString());

            if (member) {
                member.user_status = status;
                member.isOnline = status === 'online';
            }
        },
        resetOrderChat: () => initialState,
        setIsSocketRoomJoined: (state, action: PayloadAction<boolean>) => {
            state.isSocketRoomJoined = action.payload;
        },
        // Internal actions used by sendOrderChatMessage thunk
        _addOptimisticMessage: (state, action: PayloadAction<IOrderChatMessage>) => {
            state.messages.push(action.payload);
        },
        _markMessageError: (state, action: PayloadAction<string>) => {
            const msg = state.messages.find((m: IOrderChatMessage) => m.id === action.payload);

            if (msg) {
                msg.hasError = true;
            }
        },
        hideMessage: (state, action: PayloadAction<string | number>) => {
            if (!state.hiddenMessageIds.includes(action.payload)) {
                state.hiddenMessageIds.push(action.payload);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrderChatMessages.pending, (state, action) => {
                const isPagination = action.meta.arg.lastId != null;

                if (!isPagination) {
                    state.isLoading = true;
                }
            })
            .addCase(fetchOrderChatMessages.fulfilled, (state, action) => {
                state.isLoading = false;
                const { messages, hasMore, lastId, isPagination, chatGroupId } = action.payload;

                if (isPagination) {
                    // Prepend older messages, deduplicating
                    const existingIds = new Set(state.messages.map((m: IOrderChatMessage) => m.id?.toString()));
                    const newMessages = messages.filter((m) => !existingIds.has(m.id?.toString()));
                    state.messages = [...newMessages, ...state.messages];
                } else {
                    state.messages = messages;
                    // Compute unread count from initial fetch only
                    state.unreadCount = messages.filter((m) => m.isUnread).length;
                }

                state.hasMoreMessages = hasMore;
                state.lastMessageId = lastId;

                // Only update chatGroupId on the initial fetch (not pagination) so it isn't cleared
                if (!isPagination && chatGroupId) {
                    state.chatGroupId = chatGroupId;
                }
            })
            .addCase(markOrderChatMessagesAsRead.pending, (state) => {
                state.isMarkingAsRead = true;
            })
            .addCase(markOrderChatMessagesAsRead.fulfilled, (state) => {
                state.isMarkingAsRead = false;
                state.unreadCount = 0;
            })
            .addCase(markOrderChatMessagesAsRead.rejected, (state) => {
                state.isMarkingAsRead = false;
            })
            .addCase(catchUpOrderChatMessages.pending, (state) => {
                state.isCatchingUp = true;
            })
            .addCase(catchUpOrderChatMessages.fulfilled, (state, action) => {
                state.isCatchingUp = false;
                (action.payload || []).forEach((raw: any) => mergeIncomingMessage(state, raw));
            })
            .addCase(catchUpOrderChatMessages.rejected, (state) => {
                state.isCatchingUp = false;
            })
            .addCase(fetchOrderChatMessages.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchOrderChatMembers.pending, (state) => {
                state.isMembersLoading = true;
            })
            .addCase(fetchOrderChatMembers.fulfilled, (state, action) => {
                state.isMembersLoading = false;
                state.members = action.payload;
            })
            .addCase(fetchOrderChatMembers.rejected, (state) => {
                state.isMembersLoading = false;
            });
    },
});

export const { addSocketMessage, incrementUnreadCount, updateMemberStatus, resetOrderChat, setIsSocketRoomJoined, hideMessage } = orderChatSlice.actions;

export const sendOrderChatMessage = createAsyncThunk(
    'orderChat/sendMessage',
    async ({ orderId, content, files, currentUser }: { orderId: string | number; content: string; files?: File[]; currentUser: any }, { dispatch, getState, rejectWithValue }) => {
        // Read chatGroupId directly from Redux state — populated by fetchOrderChatMessages
        const chatGroupId = (getState() as any).orderChat.chatGroupId || '';
        const tempId = `temp-${Date.now()}`;

        const optimisticAttachments =
            files && files.length > 0
                ? files.map((file) => ({
                      orignalName: file.name,
                      name: file.name,
                      fileSize: file.size,
                      fileType: file.type.startsWith('image/') ? 'image' : 'file',
                      _blobUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
                  }))
                : [];

        const optimisticMessage: IOrderChatMessage = {
            id: tempId,
            content,
            userInfo: {
                id: currentUser?.id || '',
                firstName: currentUser?.firstName || currentUser?.first_name || '',
                lastName: currentUser?.lastName || currentUser?.last_name || '',
                imageUrl: currentUser?.imageUrl || '',
                image: currentUser?.image || '',
            },
            createdAt: new Date().toISOString(),
            isOptimistic: true,
            hasError: false,
            messageType: files && files.length > 0 ? 'file' : 'text',
            attachments: optimisticAttachments,
            _fileObjects: files,
            senderType: 'customer',
            chatType: 'customer',
        };

        dispatch(orderChatSlice.actions._addOptimisticMessage(optimisticMessage));

        try {
            const messageType = files && files.length > 0 ? files[0]?.type || 'file' : 'text';
            const authorizationToken = localStorage.getItem('token') || undefined;

            await sendCustomerMessage(
                {
                    entityId: Number(orderId),
                    entityType: 'order',
                    content: { message: content },
                    participantType: 'customer',
                    senderId: currentUser?.id || '',
                    groupId: chatGroupId || '',
                    chatType: 'customer',
                    messageType,
                },
                files && files.length > 0 ? files : undefined,
                authorizationToken
            );

            return { tempId };
        } catch (error: any) {
            dispatch(orderChatSlice.actions._markMessageError(tempId));

            return rejectWithValue({ tempId, error: error?.message });
        }
    }
);

export default orderChatSlice.reducer;
